import {match} from 'ts-pattern'
import {notNullOrEmpty, keys} from 'shared/collection'
import {NotNull, notNull, Prettify} from 'shared/type'
import {doSafe, err, ok} from 'shared/promise'
import {zx} from 'shared/type'
import {
  QuestionCfgCommon,
  Forecast as F,
  zSubqCommonCfg,
  zSubqType,
  zQuestionRow,
  QuestionRow,
  QuestionCfg,
  SubqCfgWithId,
  zQuestionCfgShape,
  QuestionResultsQuestion
} from 'shared/forecast'
import {okOrShowError} from 'components/notify'
import {usePortal} from 'components/portal/portal'
import {OnDbChangeParams, UseQuerySbSyncProps, useQuerySbSync} from 'utils/db/useQuerySb'
import {isString, mapToObj, omit, pick, sortBy, sumBy, values} from 'remeda'
import {ForecastFetched, UseForecastGroupApp, UseForecasts, useForecasts} from './useForecasts'
import {useDbForm} from 'components/hooks/useDbForm'
import {useMemo} from 'react'
import {z, ZodSchema} from 'zod'
import {useUIState, useUIItem, UIStateItemProps, UIStateProps} from 'components/hooks/useUIState'
import {SubqItem} from './subq'
import {ForecastProgress} from './progress'
import {TsLatest} from './QuestionTimeline'
import {sb} from 'utils/db'
import {Notify} from 'api/notify'
import {questionSortTimestamp} from 'shared/forecast/questionOrder'
import {useQuestionContext} from './questionComponentTypes'
import {MyProfile} from 'components/portal/profileTypes'

const zQuestionPageRow = z.object({
  id: z.string(),
  workspace_id: z.string(),
  created_at: z.string(),
  created_by: z.string(),
  deleted: z.boolean().default(false),
  // no upgrades, but none are needed
  cfg: z.object(
    pick(zQuestionCfgShape, [
      'status',
      'title',
      'subTitle',
      'owner',
      'tasked',
      'taskedSlots',
      'due',
      'subquestionOrder',
      'publish',
      'liveView',
      'statusAt',
      'attachments'
    ])
  ),
  subquestions: z
    .array(
      zSubqCommonCfg.pick({title: true, status: true}).extend({
        id: z.string(),
        created_at: zx.nullish.string,
        subquestionType: zSubqType,
        parent_id: zx.nullish.string
      })
    )
    .default([]),
  unread_comments: z.number().optional(),
  total_comments: z.number().optional(),
  last_viewed_at: zx.nullish.date,
  forecasters_total: z.number().optional(),
  forecasts_from: zx.nullish.date,
  forecasts_to: zx.nullish.date
})

export type QuestionPageRow = z.infer<typeof zQuestionPageRow>

export type QuestionTasked = {tasked: boolean; progress?: ForecastProgress}
export const zQuestionUserFilter = z.object({showDraft: zx.nullish.bool, question_id: zx.nullish.string})
export type QuestionUserFilter = z.infer<typeof zQuestionUserFilter>

export const useQuestionsPageQuery = ({
  userFilter,
  onDbChange
}: {
  userFilter?: QuestionUserFilter
  onDbChange?: (p: OnDbChangeParams) => void
}) => {
  const {profile} = usePortal()
  if (!profile) throw new Error('profile not loaded')

  const q = useQuerySbSync({
    table: 'question',
    schema: zQuestionPageRow,
    filter: userFilter?.question_id ? {id: userFilter.question_id} : {workspace_id: profile.workspace_id},
    clientFilter: q =>
      !q.deleted &&
      (userFilter?.showDraft || !['draft', 'draft-admin'].includes(q.cfg.status) || q.created_by == profile.id),
    selectFrom: sb.rpc('questions_page', {
      ws_id: profile.workspace_id,
      q_ids: userFilter?.question_id ? [userFilter.question_id] : null!
    }),
    options: {enabled: profile?.workspace_id != null},
    onDbChange
  })

  const questions = useMemo(
    () => (q.data == null ? undefined : sortBy(q.data, [q => questionSortTimestamp(q), 'desc'])),
    [q.data]
  )

  return {questions, ...pick(q, ['isLoading', 'isError', 'error', 'refetch', 'update', 'updateAll'])}
}

export const useQuestionQuery = <TPick = unknown,>({
  question_id: id,
  pick,
  ...props
}: {question_id: string | undefined} & Pick<
  UseQuerySbSyncProps<(typeof zQuestionRow)['shape'], TPick>,
  'options' | 'onFetched' | 'pick' | 'debugName'
>) => {
  const query = useQuerySbSync({
    table: 'question',
    schema: zQuestionRow,
    filter: {id, deleted: false},
    pick,
    options: {...props.options, enabled: id != null && (props.options?.enabled ?? true)},
    ...props
  })
  const {isError} = query

  const data = match({isError, data: query.data})
    .with({isError: true}, () => err(`error loading question: ${query.error}`))
    .with({data: []}, () => err('no question found'))
    .otherwise(({data}) => (!data ? undefined : ok(data[0])))

  return {data, ...omit(query, ['data'])}
}

export const useQuestionForm = <TPick = unknown,>({
  question_id,
  pick: pickCfg,
  disabled
}: {
  question_id: string
  pick?: (q: QuestionCfg) => TPick
  disabled?: boolean
}) => {
  type TCfg = unknown extends TPick ? QuestionCfg : Prettify<TPick & Pick<QuestionCfg, 'owner'>>
  const {profile, readonly: portalReadonly} = usePortal()
  const readonly = !profile?.hasPermission('question-edit') || portalReadonly
  const rowPick =
    pickCfg == null ? undefined : (
      (q: QuestionRow) => ({...pick(q, ['created_by', 'created_at']), cfg: {...pickCfg(q.cfg), owner: q.cfg.owner}})
    )
  type TRow =
    unknown extends TPick ? QuestionRow : Prettify<Pick<QuestionRow, 'created_by' | 'created_at'> & {cfg: TCfg}>
  const crud = useQuestionQuery({question_id: question_id, pick: rowPick, options: {enabled: !disabled}})
  const question: TRow | undefined = crud.data?.ok ? (crud.data as any) : undefined
  const form = useDbForm<TCfg>(
    question ?
      {
        stored: question.cfg as TCfg,
        autoSave: readonly ? undefined : {id: question_id, table: 'question'},
        debounceSave: 2000,
        onUpdate: async ({delta}) => {
          if (!question_id || !profile.workspace.advancedUI) return
          const dueDeltas = keys(delta).filter(k => (isString(k) && k.startsWith('due.')) || k == 'status')
          if (notNullOrEmpty(dueDeltas))
            Notify.onQuestionChange({part: 'due', question_id}).then(r =>
              okOrShowError(r, 'error when notifying server on question change')
            )
        }
      }
    : {}
  )

  const isOwner = crud.data?.ok ? isQuestionOwner(crud.data, profile) : undefined
  return {form, readonly, crud, isOwner, question}
}

export const isQuestionOwner = (
  question: {cfg: Pick<QuestionCfg, 'owner'>; created_by: string},
  profile: Pick<MyProfile, 'id'>
) => (question.cfg.owner ?? question.created_by) == profile?.id

export type UseQuestionQuery = ReturnType<typeof useQuestionQuery>

export const questionValidForSave = (q: Pick<QuestionCfgCommon, 'title'>) => notNullOrEmpty(q.title)

export const useQuestionUIItem = <ZCfg extends ZodSchema<any>, Initial extends z.infer<ZCfg> | null = z.infer<ZCfg>>({
  question_id,
  mode,
  ...props
}: {
  question_id?: string
} & Pick<NotNull<UIStateProps['filter']>, 'subPath'> &
  Pick<UIStateProps, 'mode'> &
  Omit<UIStateItemProps<ZCfg>, 'state'> & {initial?: Initial}) => {
  const type = props.type ?? props.schema.description
  if (!type) throw new Error('type is required either as a prop or in the schema description')
  const state = useQuestionUIState({
    question_id,
    mode,
    filter: {types: [type], subPath: props.subPath},
    debugName: props.debugName
  })
  return useUIItem({state, ...props})
}

export const useQuestionUIItems = <Z extends ZodSchema<any>>({
  mode,
  schema,
  type: typeRaw,
  subPath
}: Pick<UIStateProps, 'mode'> & {schema: Z; type?: string; subPath?: string[]}) => {
  const type = typeRaw ?? schema.description
  if (!type) throw new Error('type is required either as a prop or in the schema description')
  const state = useQuestionUIState({mode, filter: {types: [type], subPath}})
  return state.list({schema, type})
}

const useQuestionUIState = ({
  question_id: question_id_raw,
  disabled,
  ...props
}: {
  question_id?: string
} & Omit<UIStateProps, 'path'>) => {
  const questionCtx = useQuestionContext()
  const question_id = question_id_raw ?? questionCtx?.question_id
  const state = useUIState({
    ...props,
    disabled: disabled || !question_id,
    path: ['question', question_id ?? '']
  })
  return state
}

export const useQuestionStats = ({question_id}: {question_id: string}) => {
  const {questions} = useQuestionsPageQuery({}) // filters by ws, not question because this is loaded everywhere allready and cached
  const questionStats = doSafe(
    questions?.find(q => q.id == question_id),
    q => pick(q, ['forecasters_total', 'forecasts_from', 'forecasts_to'])
  )
  return questionStats
}

export const useQuestionForecasts = ({
  question_id,
  timestamps: timestampsRaw
}: {
  question_id: string
  timestamps?: string[]
}) => {
  const {profile} = usePortal()
  const {data: row} = useQuestionQuery({
    question_id,
    pick: r =>
      pick(r.cfg, ['forecastType', 'publish', 'disableQuestionForecast', 'amountType', 'disableQuestionForecast'])
  })
  const cfg = row?.ok ? row : undefined
  const timestamps = doSafe(
    timestampsRaw?.map(t => ({
      ts: t == TsLatest ? undefined : Number.parseInt(t),
      tsName: cfg?.publish.publications[t.toString()]?.name
    })),
    t =>
      t.length == 0 || (t.length == 1 && t[0]?.ts == null) ?
        undefined
      : sortBy(t, [t => t.ts ?? Number.MAX_SAFE_INTEGER, 'desc']) // if its just latest, re-use the existing forecasts from useQ
  )

  const uForecasts = useForecasts({
    filter: {question_id, timestamps: [undefined, ...(timestamps ?? []).map(t => t.ts).filter(notNull)]},
    disabled: cfg == null || cfg.disableQuestionForecast
  })

  /** forecasts just for this question */
  const {forecasts, forecastGroups} = useMemo(() => {
    const forecasts =
      !cfg?.disableQuestionForecast && cfg ?
        uForecasts.getForecasts(
          match(cfg)
            .with(
              {forecastType: 'amount'},
              ({forecastType, amountType}) => ({question_id, type: forecastType, amountType}) as const
            )
            .with({forecastType: 'likelihood'}, ({forecastType}) => ({question_id, type: forecastType}) as const)
            .exhaustive()
        )
      : undefined
    const forecastGroups = forecasts && !cfg?.disableQuestionForecast ? [forecasts] : []
    return {forecasts, forecastGroups}
  }, [uForecasts.getForecasts, question_id, cfg?.disableQuestionForecast])

  return {forecasts, forecastGroups}
}

export const questionResolutionForecasts = ({
  question_id,
  question: q,
  uForecasts,
  subqs
}: {
  question_id: string
  question: QuestionResultsQuestion
  uForecasts: UseForecasts
  subqs: SubqItem[]
}) => {
  // const qForResolutions = useQuestionResultsQuestion(data?.ok ? {id: question_id, ...data.cfg} : undefined)
  const {getResolutionForecasts, progress} = useMemo(() => {
    const resolutionForecastsBySq = mapToObj(
      subqs
        .map(sq => ({
          sq,
          parentSubq:
            sq.parent_id ?
              doSafe(
                subqs.find(s => s.id == sq.parent_id),
                ({id, cfg}) => ({id, ...cfg}) as SubqCfgWithId
              )
            : undefined
        }))
        .filter(({sq, parentSubq}) => sq.cfg.status == 'approved' && (!sq.parent_id || parentSubq)),
      ({sq, parentSubq}) => [
        sq.id,
        F.getResolutionForecasts<ForecastFetched, UseForecastGroupApp>({
          getForecasts: uForecasts.getForecasts,
          question: q,
          subq: sq.cfg,
          sqId: sq.id,
          parentSubq
        })
      ]
    )
    const allResolution = values(resolutionForecastsBySq)
      .flat()
      .filter(r => r.resolution.enableForecasts)
    const progress = {
      total: (q.disableQuestionForecast ? 0 : 1) + allResolution.length,
      completed:
        sumBy(allResolution, r => (r.group.myForecast != null && F.shouldShow(r.group.myForecast) ? 1 : 0)) +
        // count the the question level forecast if it's enabled
        ((
          F.shouldShow(
            uForecasts.getForecasts(
              match(q)
                .with(
                  {forecastType: 'amount'},
                  ({forecastType, amountType}) => ({question_id, type: forecastType, amountType}) as const
                )
                .with({forecastType: 'likelihood'}, ({forecastType}) => ({question_id, type: forecastType}) as const)
                .exhaustive()
            )?.myForecast
          ) && !q.disableQuestionForecast
        ) ?
          1
        : 0)
    }

    const getResolutionForecasts = (subquestionId: string) => resolutionForecastsBySq[subquestionId]

    return {getResolutionForecasts, progress}
  }, [subqs, uForecasts.getForecasts, q])

  return {getResolutionForecasts, progress}
}
